import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Image as ImageIcon, Pencil, X, Check } from "lucide-react";
import { fetchAll, createItem, updateItem, deleteItem, uploadImage, resolveImg } from "../adminApi";
import { useToast } from "../../hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";

/**
 * Generic admin CRUD module driven by a field schema.
 * fieldTypes: text | textarea | number | select | switch | image | tags
 */
export default function CrudModule({ resource, title, fields, emptyItem, renderRow, renderActions, onLogout }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyItem);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await fetchAll(resource)); }
    catch (e) { toast({ title: "Session expired", description: "Please log in again." }); onLogout && onLogout(); }
    finally { setLoading(false); }
  }, [resource, onLogout, toast]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const resetForm = () => { setForm(emptyItem); setEditingId(null); };

  const onImage = async (key, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast({ title: "Image too large", description: "Please use an image under 6MB." }); return; }
    setUploading(key);
    try {
      const { url } = await uploadImage(file);
      set(key, url);
    } catch (err) {
      toast({ title: "Upload failed", description: "Please try again or paste a URL instead." });
    } finally { setUploading(null); e.target.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (editingId) {
        await updateItem(resource, editingId, payload);
        toast({ title: "Saved", description: `${title} updated.` });
      } else {
        await createItem(resource, { ...payload, order: items.length });
        toast({ title: "Added", description: `New ${title.toLowerCase()} is live.` });
      }
      resetForm();
      await load();
    } catch (e2) {
      toast({ title: "Couldn't save", description: "Please try again." });
    } finally { setSaving(false); }
  };

  const startEdit = (item) => {
    const next = { ...emptyItem };
    fields.forEach((f) => { next[f.key] = item[f.key] ?? emptyItem[f.key]; });
    setForm(next);
    setEditingId(item.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleActive = async (item) => { await updateItem(resource, item.id, { active: !item.active }); load(); };
  const remove = async (item) => { await deleteItem(resource, item.id); toast({ title: "Deleted" }); load(); };

  return (
    <div className="grid lg:grid-cols-5 gap-8 items-start">
      {/* Form */}
      <form onSubmit={submit} className="lg:col-span-2 bg-[#0b241a] border border-white/10 rounded-2xl p-7 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display uppercase text-2xl text-white flex items-center gap-2">
            {editingId ? <Pencil className="w-5 h-5 text-sc-lime" /> : <Plus className="w-5 h-5 text-sc-lime" />}
            {editingId ? `Edit ${title}` : `New ${title}`}
          </h2>
          {editingId && (
            <button type="button" onClick={resetForm} className="text-white/50 hover:text-sc-lime inline-flex items-center gap-1 text-sm font-grotesk"><X className="w-4 h-4" /> Cancel</button>
          )}
        </div>

        {fields.map((f) => (
          <div key={f.key}>
            {f.type !== "switch" && <label className="lbl">{f.label}{f.hint && <span className="text-white/40"> {f.hint}</span>}</label>}

            {f.type === "text" && (
              <input value={form[f.key] || ""} maxLength={f.max} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder} className="adm-input" />
            )}

            {f.type === "number" && (
              <input type="number" value={form[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value === "" ? "" : Number(e.target.value))} placeholder={f.placeholder} className="adm-input" />
            )}

            {f.type === "textarea" && (
              <>
                <textarea rows={3} value={form[f.key] || ""} maxLength={f.max} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder} className="adm-input resize-none" />
                {f.max && <span className="text-[11px] text-white/40 font-grotesk">{f.max - (form[f.key] || "").length} left</span>}
              </>
            )}

            {f.type === "tags" && (
              <>
                <textarea rows={4} value={(form[f.key] || []).join("\n")} onChange={(e) => set(f.key, e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} placeholder={f.placeholder || "One item per line"} className="adm-input resize-none" />
                <span className="text-[11px] text-white/40 font-grotesk">One per line</span>
              </>
            )}

            {f.type === "select" && (
              <Select value={form[f.key]} onValueChange={(v) => set(f.key, v)}>
                <SelectTrigger className="adm-input"><SelectValue placeholder={f.placeholder} /></SelectTrigger>
                <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            )}

            {f.type === "image" && (
              <div className="space-y-2">
                <input value={typeof form[f.key] === "string" && !String(form[f.key]).startsWith("data:") && !String(form[f.key]).startsWith("/api/") ? form[f.key] : ""} onChange={(e) => set(f.key, e.target.value)} placeholder="Paste image URL…" className="adm-input" />
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer inline-flex items-center gap-2 bg-[#06170f] border border-white/15 hover:border-sc-lime rounded-lg px-4 py-2 text-white/80 font-grotesk text-sm transition-colors">
                    {uploading === f.key ? <Loader2 className="w-4 h-4 text-sc-lime animate-spin" /> : <ImageIcon className="w-4 h-4 text-sc-lime" />} {uploading === f.key ? "Uploading…" : "Upload"}
                    <input type="file" accept="image/*" onChange={(e) => onImage(f.key, e)} className="hidden" />
                  </label>
                  {form[f.key] && (
                    <div className="flex items-center gap-2">
                      <img src={resolveImg(form[f.key])} alt="preview" className="h-10 w-16 object-contain bg-white/5 rounded" />
                      <button type="button" onClick={() => set(f.key, null)} className="text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {f.type === "switch" && (
              <div className="flex items-center justify-between">
                <span className="lbl mb-0">{f.label}</span>
                <Switch checked={!!form[f.key]} onCheckedChange={(v) => set(f.key, v)} />
              </div>
            )}
          </div>
        ))}

        <button type="submit" disabled={saving} className="d2-btn w-full py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {editingId ? "Save Changes" : `Add ${title}`}
        </button>
      </form>

      {/* List */}
      <div className="lg:col-span-3">
        <h2 className="font-display uppercase text-2xl text-white mb-4">{title}s ({items.length})</h2>
        {loading ? (
          <div className="text-white/50 flex items-center gap-2 font-grotesk"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-white/50 font-grotesk bg-[#0b241a] border border-white/10 rounded-2xl p-8 text-center">Nothing here yet. Add your first one!</div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <div key={item.id} className={`bg-[#0b241a] border rounded-xl p-4 flex items-center gap-4 ${item.active ? "border-white/10" : "border-white/5 opacity-60"}`}>
                {renderRow(item)}
                <div className="flex items-center gap-3 shrink-0 ml-auto">
                  {renderActions && renderActions(item)}
                  <Switch checked={!!item.active} onCheckedChange={() => toggleActive(item)} />
                  <button onClick={() => startEdit(item)} className="text-white/40 hover:text-sc-lime"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(item)} className="text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
